import {
  usePostWithdrawal,
  WithdrawalStatus,
} from '@/context/services/office_withdrawal/OfficeWithdrawal.service';
import { OfficeWithdrawalRequestWithdrawalForm } from '@/context/services/office_withdrawal/type';
import { useErrorTrackingSystem } from '@/hooks/useErrorTrackingSystem';
import { useTranslation } from '@/i18n';
import { useToastAPI } from '@moneyforward/ap-frontend-components';
import { useCallback } from 'react';
import { SubmitHandler } from 'react-hook-form';

type Props = {
  setLoading: (isLoading: boolean) => void;
  setInlineError: (error: Error) => void;
};

export const useSubmitOfficeWithdrawal = ({
  setLoading,
  setInlineError,
}: Props) => {
  const { mutateAsync } = usePostWithdrawal();
  const { send } = useErrorTrackingSystem();
  const { t } = useTranslation();
  const notify = useToastAPI();

  const showErrorMessages = useCallback(
    (message?: string) => {
      notify.error({
        title: message || t('system_error'),
        duration: 3,
      });
    },
    [notify, t]
  );

  const onSubmitWithdrawal: SubmitHandler<OfficeWithdrawalRequestWithdrawalForm> =
    useCallback(
      async (form) => {
        try {
          setLoading(true);
          const resp = await mutateAsync(form);
          if (resp.status === WithdrawalStatus.SUCCESS) {
            window.location.href = resp.redirect_url;
            return;
          }
          if (resp.status === WithdrawalStatus.FORBIDDEN_MIMIC_MODE) {
            setLoading(false);
            setInlineError(
              new Error(resp?.error?.messages?.join('\n') || t('system_error'))
            );
            return;
          }
          if (resp.status === WithdrawalStatus.FORBIDDEN && resp.redirect_url) {
            location.href = resp.redirect_url;
            return;
          }
          setLoading(false);
          showErrorMessages(
            resp?.error?.messages?.join('\n') || t('system_error')
          );
        } catch (error) {
          if (error instanceof Error && error?.name === 'AxiosError') {
            send(error.message);
          } else {
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
            send(error as Error);
          }
          setLoading(false);
          showErrorMessages(t('system_error'));
        }
      },
      [mutateAsync, showErrorMessages, t, send, setLoading, setInlineError]
    );

  return { onSubmitWithdrawal };
};
